/**
 * @format
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Chip,
  Avatar,
} from '@mui/material';
import {
  Person,
  Email,
  Badge as BadgeIcon,
  Style,
  Info,
  Campaign,
} from '@mui/icons-material';
import { getUserInfo } from '../../../core/services/user.service';
import { getAllJobTitles } from '../../../core/services/jobTitle.service';

const DetailItem = ({ icon, label, value }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.2 }}>
    <Avatar sx={{ width: 22, height: 22 }}>
      {React.cloneElement(icon, {
        sx: { fontSize: 14, color: 'primary.main' },
      })}
    </Avatar>

    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', lineHeight: 1 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={500}
        sx={{ overflowWrap: 'anywhere' }}
      >
        {value || '—'}
      </Typography>
    </Box>
  </Stack>
);

const UserDetails = ({ user, userRoles = [] }) => {
  const [jobTitles, setJobTitles] = useState([]);
  const [pastCampaigns, setPastCampaigns] = useState([]);

  const getJobTitles = async function () {
    const assignedTitles = await getUserInfo(user.uname);
    setPastCampaigns(
      Array.isArray(assignedTitles?.projectWorkedOn)
        ? assignedTitles.projectWorkedOn
        : []
    );
    if (assignedTitles?.jobTitles?.length) {
      const jobTitles = await getAllJobTitles();
      const jobTitleNames = assignedTitles?.jobTitles?.map(
        (jobTitle) =>
          jobTitles?.find((jt) => jt.jobTitleId === jobTitle)?.jobTitleName
      );
      setJobTitles(jobTitleNames || ['No Job title assigned']);
    } else {
      setJobTitles([]);
    }
  };

  useEffect(() => {
    getJobTitles();
  }, [user]);
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        background: 'rgba(255,255,255,0.05)',
        color: 'white',
      }}
    >
      <Stack alignItems="center" spacing={0.8} mb={1.5}>
        <Avatar
          src={user.profileImageUrl || undefined}
          sx={{
            width: 48,
            height: 48,
            bgcolor: 'primary.main',
            fontSize: 20,
          }}
        >
          {user.firstName?.charAt(0) || <Person />}
        </Avatar>

        <Box textAlign="center">
          <Typography variant="subtitle2" fontWeight={600}>
            {user.name}
          </Typography>

          <Chip
            label={user.enabled ? 'Active' : 'Inactive'}
            size="small"
            color={user.enabled ? 'success' : 'default'}
            sx={{ mt: 0.5 }}
          />
        </Box>
      </Stack>

      <Divider sx={{ mb: 1.5 }} />

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Stack spacing={1.2} sx={{ flexGrow: 1 }}>
          <DetailItem icon={<Info />} label="Username" value={user.uname} />
          <DetailItem
            icon={<Person />}
            label="Name"
            value={
              user.firstName + ' ' + user.lastName
                ? user.firstName + ' ' + user.lastName
                : 'N/A'
            }
          />
          <DetailItem icon={<Email />} label="Email" value={user.email} />
        </Stack>
        <Box sx={{ flexGrow: 1 }}>
          <Stack spacing={1.2}>
            <DetailItem
              icon={<Style />}
              label="Job Title"
              value={jobTitles.join(', ')}
            />
          </Stack>
          <Stack spacing={0.6}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar sx={{ width: 22, height: 22 }}>
                <BadgeIcon sx={{ fontSize: 14, color: 'primary.main' }} />
              </Avatar>

              <Typography variant="caption" color="text.secondary">
                Assigned Role
              </Typography>
            </Stack>

            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', pl: 3 }}>
              {userRoles.length ? (
                userRoles.map((role) => (
                  <Chip
                    key={role.id}
                    label={role.name}
                    size="small"
                    variant="outlined"
                    sx={{ color: 'white' }}
                  />
                ))
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No roles assigned
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Stack spacing={0.6}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar sx={{ width: 22, height: 22 }}>
            <Campaign sx={{ fontSize: 14, color: 'primary.main' }} />
          </Avatar>

          <Typography variant="caption" color="text.secondary">
            Past Campaign
          </Typography>
        </Stack>

        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', pl: 3 }}>
          {pastCampaigns.length ? (
            pastCampaigns.map((campaign) => (
              <Chip
                key={campaign}
                label={campaign}
                size="small"
                variant="outlined"
                sx={{ color: 'white' }}
              />
            ))
          ) : (
            <Typography variant="caption" color="text.secondary">
              No past campaigns
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};

export default UserDetails;
